import { lazy, Suspense, useEffect } from 'react';

import { Row } from '@opendoor/atoms/box';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router';

import WelcomeLeadPage from '../../src/components/WelcomeLeadPage';
import WelcomeRedirectPage from '../../src/components/WelcomeRedirectPage';
import { FullPageHomeLoader } from '../components/FullPageHomeLoader';
import PublicNotificationPreferences from '../components/PublicNotificationPreferences';
import trackEvent from '../helpers/trackEvent';
import { DEVELOPER_DASHBOARD_NAME } from './DeveloperDashboard/DeveloperDashboard';
import ErrorPage from './ErrorPage';
import NotFound from './NotFound';
import PrivateRoute from './PrivateRoute';
import { REFERRAL_DASHBOARD_NAME } from './ReferralDashboard/ReferralDashboard';
import {
  DEVELOPER_DASHBOARD_ROUTE,
  ERROR_PAGE_ROUTE,
  REFERRAL_DASHBOARD_ROUTE,
  SIGN_UP_ROUTE,
  SUBSCRIPTION_PREFERENCES_ROUTE,
  WELCOME_LEAD_ROUTE,
  WELCOME_REDIRECT_ROUTE,
} from './routes';
import SignUp from './SignUp/SignUp';

// Code-split major dashboard routes for better performance
const DeveloperDashboard = lazy(() => import('./DeveloperDashboard/DeveloperDashboard'));
const ReferralDashboard = lazy(() => import('./ReferralDashboard/ReferralDashboard'));
const PurchaseAgreementPage = lazy(() => import('./PurchaseAgreementPage'));

// Component to track page views
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackEvent('view', 'page', { path: location.pathname });
  }, [location.pathname]);

  return null;
};

const AppRoutes = () => {
  const renderDevDashBoardWithProps = () => {
    return <DeveloperDashboard testMode={true} />;
  };

  const renderDevLogo = () => (
    <Row>
      <img
        src="https://images.opendoor.com/source/s3/imgdrop-production/065e19554f80403eaa59d45a859d2df2.png?service=partnership-dashboard-fe"
        alt="Opendoor"
        height="25"
        // Preflight's `img { height: auto }` outranks the `height` attribute,
        // so the intended size has to be restated as a style.
        style={{ height: 25 }}
      />
    </Row>
  );

  return (
    <BrowserRouter>
      <PageTracker />
      <Suspense fallback={<FullPageHomeLoader />}>
        <Routes>
          <Route path={ERROR_PAGE_ROUTE} element={<ErrorPage />} />
          <Route path={SIGN_UP_ROUTE}>
            <Route index element={<SignUp />} />
            <Route path="*" element={<SignUp />} />
          </Route>
          <Route path={SUBSCRIPTION_PREFERENCES_ROUTE}>
            <Route index element={<PublicNotificationPreferences />} />
            <Route path="*" element={<PublicNotificationPreferences />} />
          </Route>
          <Route path={WELCOME_LEAD_ROUTE + '/:sellerInputId'} element={<WelcomeLeadPage />} />
          <Route path={WELCOME_REDIRECT_ROUTE} element={<WelcomeRedirectPage />} />
          <Route
            path="/purchase-agreement/:referralUUID"
            element={
              <PrivateRoute
                component={PurchaseAgreementPage}
                isAuthorized={(human) => human.has_partnership_referral_dashboard}
              />
            }
          />
          {/* Developer dashboard routes */}
          <Route
            path={DEVELOPER_DASHBOARD_ROUTE + '/test/:tab?'}
            element={
              <PrivateRoute
                component={renderDevDashBoardWithProps}
                isAuthorized={(human) => human.has_partnership_developer_dashboard}
                dashboardName={DEVELOPER_DASHBOARD_NAME}
                renderLogo={renderDevLogo}
              />
            }
          />
          <Route
            path={DEVELOPER_DASHBOARD_ROUTE + '/:tab?'}
            element={
              <PrivateRoute
                component={DeveloperDashboard}
                isAuthorized={(human) => human.has_partnership_developer_dashboard}
                dashboardName={DEVELOPER_DASHBOARD_NAME}
                renderLogo={renderDevLogo}
              />
            }
          />
          <Route path={REFERRAL_DASHBOARD_ROUTE}>
            <Route
              index
              element={
                <PrivateRoute
                  component={ReferralDashboard}
                  isAuthorized={(human) => human.has_partnership_referral_dashboard}
                  dashboardName={REFERRAL_DASHBOARD_NAME}
                />
              }
            />
            <Route
              path="*"
              element={
                <PrivateRoute
                  component={ReferralDashboard}
                  isAuthorized={(human) => human.has_partnership_referral_dashboard}
                  dashboardName={REFERRAL_DASHBOARD_NAME}
                />
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <PrivateRoute
                components={[
                  {
                    path: REFERRAL_DASHBOARD_ROUTE,
                    isAuthorized: (human) => human.has_partnership_referral_dashboard,
                  },
                  {
                    path: DEVELOPER_DASHBOARD_ROUTE,
                    isAuthorized: (human) => human.has_partnership_developer_dashboard,
                  },
                ]}
              />
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRoutes;
